import { useState } from "react";
import { useLang } from "@/components/LanguageContext";
import ScrollReveal from "@/components/ScrollReveal";

// PwC "At PwC, possibility has no boundaries" — left-aligned big title + intro on top,
// then a horizontal tab strip with an animated orange underline indicator,
// then a content panel: image-left + bullet-list right.
export default function PossibilityTabs({ kicker, title, intro, image, tabs = [] }) {
  const { lang } = useLang();
  const [active, setActive] = useState(0);
  const current = tabs[active];

  if (tabs.length === 0) return null;

  return (
    <section className="bg-[var(--abs-cream)] py-20 md:py-28" data-testid="possibility-tabs">
      <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
        <ScrollReveal>
          {kicker && (
            <div className="text-[12px] uppercase tracking-[0.22em] text-[var(--abs-orange)] mb-6">{kicker}</div>
          )}
          <h2 className="serif text-5xl md:text-6xl lg:text-[72px] leading-[1.04] tracking-tight max-w-3xl">
            {title}
          </h2>
          {intro && (
            <p className="mt-12 text-[16.5px] leading-[2] max-w-xl text-[var(--abs-ink)]">
              {intro}
            </p>
          )}
        </ScrollReveal>

        {/* Tab strip */}
        <div className="mt-16 border-b border-[var(--abs-line)] overflow-x-auto" role="tablist">
          <div className="flex gap-10 lg:gap-16 min-w-max">
            {tabs.map((tab, idx) => (
              <button
                key={idx}
                role="tab"
                aria-selected={active === idx}
                onClick={() => setActive(idx)}
                data-testid={`tab-${idx}`}
                className={`relative pb-5 text-[15px] md:text-[16px] whitespace-nowrap transition-colors ${active === idx ? "text-[var(--abs-ink)] font-medium" : "text-[var(--abs-mute)] hover:text-[var(--abs-ink)]"}`}
              >
                {tab.heading}
                <span
                  className={`absolute left-0 right-0 -bottom-px h-[3px] transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${active === idx ? "bg-[var(--abs-orange)] scale-x-100" : "bg-transparent scale-x-0"}`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Panel */}
        <div className="mt-12 lg:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <ScrollReveal key={`img-${active}`} className="lg:col-span-6">
            <div className="img-frame aspect-[4/3] bg-white">
              <img src={current.image || image} alt={current.heading} className="w-full h-full object-cover" />
            </div>
          </ScrollReveal>
          <ScrollReveal key={`panel-${active}`} delay={80} className="lg:col-span-6">
            {current.body && (
              <p className="text-[17px] leading-[1.7] text-[var(--abs-ink)] mb-9">{current.body}</p>
            )}
            {Array.isArray(current.points) && current.points.length > 0 && (
              <ul className="space-y-7">
                {current.points.map((p, j) => (
                  <li key={j} className="flex items-start gap-4 text-[16px] leading-[1.6] text-[var(--abs-ink)]">
                    <span className="mt-[10px] block w-[10px] h-[10px] bg-[var(--abs-ink)] shrink-0" aria-hidden />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            )}
            {Array.isArray(current.list) && current.list.length > 0 && (
              <div className="space-y-7">
                {current.list.map((it, j) => (
                  <div key={j}>
                    <h4 className="serif text-[22px] mb-2 text-[var(--abs-ink)]">{it.heading}</h4>
                    <p className="text-[15.5px] leading-[1.7] text-[var(--abs-mute)]">{it.body}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollReveal>
        </div>
      </div>
    </section>
  );
}
